import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import Button from "../../common/Button";
import { Box, Grid, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { Cancel } from "@mui/icons-material";
import { FileUp, Save } from "lucide-react"; // Modern icons
import { convertBase64 } from "../../../utils/Utility";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { BAA_CONTRACT_TYPES, pdf } from "../../../constants/applicationConstants";
import { uploadSignedBaa, UploadBaaInfo } from "../../../redux/slice/baa/createBusinessAgreementSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { setSingleAlertObj, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { useModalStyles } from "../../common/useModalStyles";

interface UploadBaaPopupProps {
  open: boolean;
  onClose: () => void;
  userEntity: "PRACTICE" | "USER";
  details: any;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const UploadBaaPopup: React.FC<UploadBaaPopupProps> = ({
  open,
  onClose,
  userEntity,
  details
}) => {
  const { control, handleSubmit, reset, setValue } = useForm<UploadBaaInfo>({});
  const dispatch = useAppDispatch();

  const {
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    tokens,
    cancelBtnSx,
    primaryBtnSx,
    iconBoxSx,
  } = useModalStyles();

  // ── Reusable FieldLabel ──
  const FieldLabel = ({ label, required = false }: { label: string; required?: boolean }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>*</Box>
      )}
    </Typography>
  );

  useEffect(() => {
    if (open) {
      if (userEntity === BAA_CONTRACT_TYPES.PRACTICE) {
        setValue("practiceId", details?.practiceId);
      } else if (userEntity === BAA_CONTRACT_TYPES.USER) {
        setValue("userId", details?.userId);
      }
    }
  }, [open, userEntity, details, setValue]);

  const onSubmit = async (data: UploadBaaInfo) => {
    const response = await dispatch(uploadSignedBaa(data));
    if (response.meta.requestStatus === 'fulfilled') {
      let successObj: SingleAlertInfo = {
        message: `Business Association Agreement for ${userEntity === BAA_CONTRACT_TYPES.PRACTICE ?
          details?.practiceName : "(subcontractor version) " + details?.userName} has been successfully uploaded.`,
        alertType: "success"
      }
      dispatch(setSingleAlertObj(successObj));
      onClosePopup();
    }
  };

  const handleDropzoneChange = async (uploadedFiles: ExtendedFile[]) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      const base64File = String(await convertBase64(uploadedFiles[0]));
      const fileName = uploadedFiles[0].name;
      const fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
      const base64Substring = base64File.includes("base64,") 
        ? base64File.split(",")[1] 
        : base64File;

      setValue('content', base64Substring);
      setValue('documentName', fileName);
      setValue('fileType', fileExtension);
      setValue('type', "BUSINESS_ASSOCIATE_AGREEMENT");
    }
  };

  const handleDeleteFile = () => {
    setValue('content', "");
    setValue('documentName', "");
    setValue('fileType', "");
    setValue('type', "");
  };

  const onClosePopup = () => {
    reset();
    onClose();
  }

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClosePopup}
      PaperProps={{ 
        sx: { 
          ...paperSx(false),
          width: 550,
          maxWidth: "95vw" 
        } 
      }}
      onClick={(event) => event.stopPropagation()}
    >
      {/* ── Header ── */}
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <FileUp size={20} color={tokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: tokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Upload Business Association Agreement
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: tokens.textSecondary, mt: 0.15 }}>
            {userEntity === BAA_CONTRACT_TYPES.USER ? "Subcontractor Version" : "Practice Standard Version"}
          </Typography>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ ...bodySx, p: 3 }}>
          <Grid container spacing={3}>
            {/* Entity Name Info */}
            <Grid item xs={12}>
              <Box sx={{ 
                background: "rgba(255, 255, 255, 0.03)", 
                p: 2, 
                borderRadius: "8px", 
                border: "1px solid rgba(255, 255, 255, 0.08)",
                display: "flex",
                alignItems: "center",
                gap: 2,
                flexWrap: "wrap",
              }}>
                <Typography sx={{ fontSize: "0.75rem", color: tokens.textSecondary, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>
                  Name
                </Typography>
                <Typography sx={{ fontSize: "1rem", color: tokens.textPrimary, fontWeight: 500 }}>
                  {userEntity === BAA_CONTRACT_TYPES.PRACTICE ? details?.practiceName : details?.userName}
                </Typography>
              </Box>
            </Grid>

            {/* Dropzone Section */}
            <Grid item xs={12}>
              <FieldLabel label="Agreement Document" required />
              <Dropzone
                name="content"
                onFileChange={() => { }}
                onFileChangeTest={handleDropzoneChange}
                onDeleteFile={handleDeleteFile}
                control={control}
                maxFileCount={1}
                allowedFileTypes={pdf}
                label="Click or drag PDF to upload agreement"
                rules={{ required: "Business Association Agreement is required." }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ── Footer ── */}
        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5, width: "100%" }}>
            <Button
              fullWidth
              type="submit"
              variant="contained"
              startIcon={<Save size={18} />}
              sx={{
                ...primaryBtnSx,
                color: "#ffffff",
                "& .MuiTypography-root": { color: "#ffffff" },
                "& .MuiButton-startIcon": { color: "#ffffff" },
              }}
            >
              Upload Agreement
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<Cancel />}
              onClick={onClosePopup}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default UploadBaaPopup;